import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  DatePicker,
  Input,
  Select,
  Space,
  Table,
  type TableColumnProps,
} from '@arco-design/web-react';
import { IconRefresh } from '@arco-design/web-react/icon';
import dayjs from 'dayjs';
import { PageHeader, StatusPill } from '@/components/ui';
import { transactionsApi } from '@/api';
import type {
  SettlementStatus,
  TransactionDirection,
  TransactionListQuery,
  TransactionRecord,
} from '@/types/transaction';
import { formatMoney } from '@/utils/money';

const { RangePicker } = DatePicker;

const directionOptions: Array<{ label: string; value: TransactionDirection | 'ALL' }> = [
  { label: '全部流向', value: 'ALL' },
  { label: '消费', value: 'PURCHASE' },
  { label: '退款', value: 'REFUND' },
];

const statusOptions: Array<{ label: string; value: SettlementStatus | 'ALL' }> = [
  { label: '全部状态', value: 'ALL' },
  { label: '处理中', value: 'PROCESSING' },
  { label: '已确认', value: 'CONFIRMED' },
  { label: '被拒绝', value: 'REJECTED' },
  { label: '已完成', value: 'COMPLETED' },
];

const statusToneMap: Record<SettlementStatus, 'processing' | 'success' | 'warning' | 'danger'> = {
  PROCESSING: 'processing',
  CONFIRMED: 'success',
  REJECTED: 'danger',
  COMPLETED: 'success',
};

export default function TransactionLogsView() {
  const [query, setQuery] = useState<TransactionListQuery>({
    page: 1,
    pageSize: 20,
  });
  const [list, setList] = useState<TransactionRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await transactionsApi.list(query);
      setList(result.items);
      setTotal(result.total);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchData().catch(() => undefined);
  }, [fetchData]);

  const updateQuery = (next: Partial<TransactionListQuery>) =>
    setQuery((prev) => ({ ...prev, ...next, page: 1 }));

  const columns: TableColumnProps<TransactionRecord>[] = [
    {
      title: '记账时间',
      dataIndex: 'bookedAt',
      width: 160,
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
    },
    { title: '脱敏卡号', dataIndex: 'maskedPan', width: 160 },
    { title: 'MCC', dataIndex: 'mcc', width: 80 },
    { title: '商户', dataIndex: 'merchantName', ellipsis: true },
    {
      title: '流向',
      dataIndex: 'direction',
      width: 80,
      render: (v: TransactionDirection) => (v === 'PURCHASE' ? '消费' : '退款'),
    },
    {
      title: '金额',
      dataIndex: 'amountCents',
      width: 120,
      align: 'right',
      render: (v: number, r) => (
        <span
          style={{
            color: r.direction === 'REFUND' ? 'var(--up-color-success)' : 'var(--up-color-text-primary)',
            fontFamily: 'SFMono-Regular, Consolas, monospace',
          }}
        >
          {r.direction === 'REFUND' ? '+' : '-'} {formatMoney(Math.abs(v))}
        </span>
      ),
    },
    {
      title: '清算手续费',
      dataIndex: 'feeCents',
      width: 120,
      align: 'right',
      render: (v: number) => formatMoney(v),
    },
    {
      title: '清算状态',
      dataIndex: 'status',
      width: 100,
      render: (v: SettlementStatus) => (
        <StatusPill tone={statusToneMap[v]}>
          {statusOptions.find((s) => s.value === v)?.label ?? v}
        </StatusPill>
      ),
    },
    {
      title: '网络交易号',
      dataIndex: 'networkTxnId',
      width: 200,
      ellipsis: true,
    },
    {
      title: '失败原因',
      dataIndex: 'failureReason',
      ellipsis: true,
      render: (v) => v || <span style={{ color: '#86909C' }}>—</span>,
    },
  ];

  return (
    <div className="up-page">
      <PageHeader
        title="财务对账明细"
        subtitle="多维筛选 + 物理分页，对接上游清算流水。任何金额展示与导出均以美元为单位"
        extra={
          <Button icon={<IconRefresh />} onClick={() => fetchData()}>
            刷新
          </Button>
        }
      />

      <div className="up-section-card" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="卡号 / 卡 ID 快速过滤"
            style={{ width: 220 }}
            onSearch={(v) => updateQuery({ cardId: v || undefined })}
          />
          <RangePicker
            onChange={(dateStrings) =>
              updateQuery({
                startDate: dateStrings?.[0] || undefined,
                endDate: dateStrings?.[1] || undefined,
              })
            }
          />
          <Select
            placeholder="流向"
            style={{ width: 140 }}
            value={query.direction ?? 'ALL'}
            onChange={(v) =>
              updateQuery({ direction: v === 'ALL' ? undefined : (v as TransactionDirection) })
            }
          >
            {directionOptions.map((o) => (
              <Select.Option key={o.value} value={o.value}>
                {o.label}
              </Select.Option>
            ))}
          </Select>
          <Select
            placeholder="清算状态"
            style={{ width: 160 }}
            value={query.status ?? 'ALL'}
            onChange={(v) =>
              updateQuery({ status: v === 'ALL' ? undefined : (v as SettlementStatus) })
            }
          >
            {statusOptions.map((o) => (
              <Select.Option key={o.value} value={o.value}>
                {o.label}
              </Select.Option>
            ))}
          </Select>
        </Space>
      </div>

      <div className="up-section-card">
        <Table
          rowKey="id"
          loading={loading}
          data={list}
          columns={columns}
          scroll={{ x: 1200 }}
          pagination={{
            total,
            current: query.page,
            pageSize: query.pageSize,
            sizeCanChange: true,
            showTotal: true,
            onChange: (page, pageSize) => setQuery((p) => ({ ...p, page, pageSize })),
          }}
        />
      </div>
    </div>
  );
}
